"use client";

import { SectionCard, StatusBadge } from "@repo/dashboard-core";
import { Alert } from "@mantine/core";
import { useTranslations } from "next-intl";

import type { PhoneCheck } from "@/lib/types/phone-checker";

/**
 * The verdict on the number itself, before any package.
 *
 * GD-55 AC2 asks for an invalid number to be flagged clearly rather than
 * returned as a partial result — and an invalid number is exactly the case
 * where every package below comes back empty. Saying so once, at the top, is
 * what stops seven empty cards reading as seven failures.
 */
export function CheckSummary({ check }: { check: PhoneCheck }) {
  const t = useTranslations("phone-checker");

  if (!check.valid) {
    return (
      <Alert color="red" radius="md" title={t("result.invalid")}>
        <p>{t("result.invalid-description")}</p>
        {check.validationErrors.length > 0 && (
          <ul className="mt-2 list-disc ps-5">
            {check.validationErrors.map((reason) => (
              <li key={reason}>{t(`validation-error.${reason}`)}</li>
            ))}
          </ul>
        )}
      </Alert>
    );
  }

  return (
    <SectionCard>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-lg font-semibold text-text-primary">
            {check.nationalFormat ?? check.phoneNumber}
          </p>
          <p className="text-sm text-text-secondary">{check.phoneNumber}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {check.countryCode && (
            <StatusBadge label={check.countryCode} variant="neutral" />
          )}
          <StatusBadge label={t("result.valid")} variant="success" />
        </div>
      </div>
    </SectionCard>
  );
}
